import React, { useCallback, useMemo } from 'react';

import { Box } from '@mui/material';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import ColorPicker from 'app/shared/components/form/ColorPicker';
import { DEFAULT_BACKGROUND_COLOR } from 'app/shared/components/form/ColorPicker/ColorPicker.styles';
import {
  AutocompleteCDS,
  RadioGroupCDS,
} from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { CheckboxRenderType } from 'constants/forms';
import {
  defaultFontFamilyOption as baseDefaultFontFamilyOption,
  fontFamilyOptions as baseFontFamilyOptions,
  horizontalAlignmentOptions,
  verticalAlignmentOptions,
} from 'constants/options';
import { ItemLabelPlacement } from 'constants/ui';

import { formsTestIds } from '../../../forms.testIds';
import DynamicFieldBoldCheckbox from '../DynamicFieldBoldCheckbox';
import {
  boldAlignedWithCheckboxViewCellStyles,
  colorPickerCellStyles,
} from '../DynamicFieldOptions.styles';

import { defaultFontSizeOption, fontSizeOptions } from './StyleViewOptions.constants';

const StyleViewOptions = ({
  isBold,
  fontSize = null,
  fontFamily = null,
  handleChange,
  isAlignmentHidden = false,
  horizontalAlignment = null,
  verticalAlignment = null,
  setInForm,
  validateField,
  checkboxRenderType = '',
  fontColor = null,
  backgroundColor = null,
  isBackgroundColorEnabled = false,
  fontFamilyOptions: fontFamilyOptionsProp,
  defaultFontFamilyOption: defaultFontFamilyOptionProp,
}) => {
  const fontFamilyOptions = fontFamilyOptionsProp ?? baseFontFamilyOptions;
  const defaultFontFamilyOption = defaultFontFamilyOptionProp ?? baseDefaultFontFamilyOption;

  const isAlignmentAvailable = !isAlignmentHidden && !!horizontalAlignment && !checkboxRenderType;
  const isVerticalAlignmentAvailable = isAlignmentAvailable && !!verticalAlignment;

  const fontSizeOption = useMemo(
    () => fontSizeOptions.find(({ value }) => value === fontSize?.value) || defaultFontSizeOption,
    [fontSize],
  );

  const fontFamilyOption = useMemo(
    () =>
      fontFamilyOptions.find(({ value }) => value === fontFamily?.value) || defaultFontFamilyOption,
    [fontFamily, fontFamilyOptions, defaultFontFamilyOption],
  );

  const horizontalAlignmentOption = useMemo(
    () => horizontalAlignmentOptions.find(({ value }) => value === horizontalAlignment?.value),
    [horizontalAlignment],
  );

  const verticalAlignmentOption = useMemo(
    () => verticalAlignmentOptions.find(({ value }) => value === verticalAlignment?.value),
    [verticalAlignment],
  );

  const onAutocompleteChange = useMemo(
    () => onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onClearBackgroundColor = useCallback(() => {
    setInForm('backgroundColor', null);
  }, [setInForm]);

  const onBackgroundColorPickerOpen = useCallback(() => {
    setInForm('backgroundColor', DEFAULT_BACKGROUND_COLOR);
  }, [setInForm]);

  return (
    <>
      {!!checkboxRenderType && (
        <>
          <Box sx={boldAlignedWithCheckboxViewCellStyles}>
            <DynamicFieldBoldCheckbox value={isBold} onChange={handleChange} />
          </Box>

          <RadioGroupCDS
            row
            dataTestId={formsTestIds.STYLE_VIEW_CHECKBOX_RENDER_TYPE_RADIO}
            label="Check Box view:"
            labelPlacement={ItemLabelPlacement.END}
            name="checkboxRenderType"
            options={Object.values(CheckboxRenderType)}
            value={checkboxRenderType}
            onChange={handleChange}
          />
        </>
      )}

      <AutocompleteCDS
        dataTestId={formsTestIds.STYLE_VIEW_FONT_FAMILY_SELECT}
        label="Font"
        name="fontFamily"
        options={fontFamilyOptions}
        value={fontFamilyOption}
        onChange={onAutocompleteChange}
      />

      <AutocompleteCDS
        dataTestId={formsTestIds.STYLE_VIEW_FONT_SIZE_SELECT}
        label="Font Size"
        name="fontSize"
        options={fontSizeOptions}
        value={fontSizeOption}
        onChange={onAutocompleteChange}
      />

      <AutocompleteCDS
        disableClearable
        dataTestId={formsTestIds.STYLE_VIEW_HORIZONTAL_ALIGNMENT_SELECT}
        isHidden={!isAlignmentAvailable}
        label="Horizontal Alignment"
        name="horizontalAlignment"
        options={horizontalAlignmentOptions}
        value={horizontalAlignmentOption}
        withSortedOptions={false}
        onChange={onAutocompleteChange}
      />

      <AutocompleteCDS
        disableClearable
        dataTestId={formsTestIds.STYLE_VIEW_VERTICAL_ALIGNMENT_SELECT}
        isHidden={!isVerticalAlignmentAvailable}
        label="Vertical Alignment"
        name="verticalAlignment"
        options={verticalAlignmentOptions}
        value={verticalAlignmentOption}
        withSortedOptions={false}
        onChange={onAutocompleteChange}
      />

      {isAlignmentAvailable && !isVerticalAlignmentAvailable && <Box />}

      <Box sx={colorPickerCellStyles}>
        <ColorPicker
          noWrapLabel
          label="Font color"
          name="fontColor"
          value={fontColor || ''}
          variant="outlined"
          onChange={handleChange}
        />
      </Box>

      {isBackgroundColorEnabled && (
        <Box sx={colorPickerCellStyles}>
          <ColorPicker
            noWrapLabel
            showPlaceholderIcon
            label="Background color"
            name="backgroundColor"
            value={backgroundColor || DEFAULT_BACKGROUND_COLOR}
            variant="outlined"
            onChange={handleChange}
            onClear={backgroundColor ? onClearBackgroundColor : null}
            onPickerOpen={onBackgroundColorPickerOpen}
          />
        </Box>
      )}
    </>
  );
};

StyleViewOptions.propTypes = {
  handleChange: PropTypes.func.isRequired,
  setInForm: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  isBold: PropTypes.bool.isRequired,
  isAlignmentHidden: PropTypes.bool,
  horizontalAlignment: PropTypes.object,
  verticalAlignment: PropTypes.object,
  fontSize: PropTypes.object,
  checkboxRenderType: PropTypes.string,
  fontColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  isBackgroundColorEnabled: PropTypes.bool,
  fontFamily: PropTypes.object,
  fontFamilyOptions: PropTypes.arrayOf(PropTypes.object),
  defaultFontFamilyOption: PropTypes.object,
};

export default StyleViewOptions;
